import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, AUTH_CHALLENGE } from '../../contexts/AuthContext';
import { getPwdRules } from '../../utils/passwordRules';

const STEP = {
  EMAIL: 'email',
  PASSWORD: 'password',
  NEW_PASSWORD: 'new_password',
  FORGOT_PASSWORD: 'forgot_password',
  RESET_PASSWORD: 'reset_password',
};

// ── Brand colors — aligned to index.css palette ────────────────────────────────
const NAVY   = '#0A1128';
const ROYAL  = '#2C5FA8';
const AQUA_BRIGHT = '#22D3EE';
const ACCENT   = '#2C5FA8';

// ── Floating dots — 40 circles spread across full viewport ───────────────────
// Colors, blur, and animation timing match nexonurse.com particle system
const DOT_DATA = [
  // col 1 (left ~0–20%)
  { left: 3,  top: 7,  size: 10, opacity: 0.55, blur: 1, dur: 8.2,  delay: 0.0,  color: '#22D3EE' },
  { left: 8,  top: 22, size: 6,  opacity: 0.45, blur: 0, dur: 10.1, delay: 3.2,  color: '#0891B2' },
  { left: 14, top: 38, size: 14, opacity: 0.40, blur: 2, dur: 7.5,  delay: 1.4,  color: '#2C5FA8' },
  { left: 5,  top: 55, size: 8,  opacity: 0.50, blur: 1, dur: 9.3,  delay: 5.1,  color: '#22D3EE' },
  { left: 11, top: 70, size: 12, opacity: 0.38, blur: 2, dur: 8.7,  delay: 2.3,  color: '#0891B2' },
  { left: 3,  top: 85, size: 6,  opacity: 0.42, blur: 0, dur: 11.2, delay: 7.0,  color: '#1E4480' },
  { left: 16, top: 93, size: 10, opacity: 0.45, blur: 1, dur: 9.8,  delay: 4.5,  color: '#22D3EE' },
  { left: 18, top: 14, size: 8,  opacity: 0.35, blur: 3, dur: 7.8,  delay: 6.2,  color: '#0891B2' },
  // col 2 (~20–40%)
  { left: 24, top: 5,  size: 12, opacity: 0.42, blur: 1, dur: 8.4,  delay: 1.8,  color: '#2C5FA8' },
  { left: 29, top: 28, size: 6,  opacity: 0.55, blur: 0, dur: 12.0, delay: 0.7,  color: '#22D3EE' },
  { left: 22, top: 48, size: 10, opacity: 0.38, blur: 2, dur: 9.6,  delay: 4.9,  color: '#0891B2' },
  { left: 35, top: 62, size: 8,  opacity: 0.50, blur: 1, dur: 7.2,  delay: 2.6,  color: '#22D3EE' },
  { left: 27, top: 75, size: 14, opacity: 0.32, blur: 3, dur: 10.5, delay: 8.1,  color: '#2C5FA8' },
  { left: 33, top: 88, size: 6,  opacity: 0.45, blur: 0, dur: 8.9,  delay: 5.7,  color: '#0891B2' },
  { left: 38, top: 17, size: 10, opacity: 0.40, blur: 1, dur: 11.3, delay: 3.4,  color: '#1E4480' },
  { left: 21, top: 35, size: 8,  opacity: 0.52, blur: 2, dur: 9.1,  delay: 0.9,  color: '#22D3EE' },
  // col 3 (~40–60%)
  { left: 44, top: 10, size: 8,  opacity: 0.40, blur: 1, dur: 8.6,  delay: 2.1,  color: '#0891B2' },
  { left: 50, top: 25, size: 12, opacity: 0.45, blur: 2, dur: 10.4, delay: 6.8,  color: '#2C5FA8' },
  { left: 42, top: 42, size: 6,  opacity: 0.58, blur: 0, dur: 7.9,  delay: 1.5,  color: '#22D3EE' },
  { left: 57, top: 58, size: 10, opacity: 0.35, blur: 2, dur: 9.7,  delay: 4.2,  color: '#0891B2' },
  { left: 47, top: 73, size: 8,  opacity: 0.48, blur: 1, dur: 8.3,  delay: 7.5,  color: '#22D3EE' },
  { left: 53, top: 88, size: 14, opacity: 0.30, blur: 3, dur: 11.6, delay: 3.0,  color: '#2C5FA8' },
  { left: 59, top: 5,  size: 6,  opacity: 0.52, blur: 0, dur: 9.2,  delay: 9.1,  color: '#0891B2' },
  { left: 45, top: 95, size: 8,  opacity: 0.42, blur: 1, dur: 7.4,  delay: 0.3,  color: '#1E4480' },
  // col 4 (~60–80%)
  { left: 64, top: 18, size: 10, opacity: 0.42, blur: 1, dur: 10.8, delay: 5.5,  color: '#22D3EE' },
  { left: 71, top: 33, size: 6,  opacity: 0.50, blur: 0, dur: 8.1,  delay: 2.7,  color: '#0891B2' },
  { left: 67, top: 50, size: 12, opacity: 0.36, blur: 2, dur: 9.4,  delay: 8.3,  color: '#2C5FA8' },
  { left: 78, top: 65, size: 8,  opacity: 0.55, blur: 1, dur: 7.6,  delay: 1.2,  color: '#22D3EE' },
  { left: 63, top: 80, size: 10, opacity: 0.38, blur: 2, dur: 11.0, delay: 6.4,  color: '#0891B2' },
  { left: 74, top: 9,  size: 6,  opacity: 0.48, blur: 0, dur: 8.7,  delay: 4.0,  color: '#1E4480' },
  { left: 70, top: 95, size: 14, opacity: 0.28, blur: 3, dur: 10.2, delay: 9.7,  color: '#2C5FA8' },
  { left: 79, top: 44, size: 8,  opacity: 0.45, blur: 1, dur: 9.9,  delay: 3.7,  color: '#22D3EE' },
  // col 5 (~80–100%)
  { left: 84, top: 12, size: 10, opacity: 0.42, blur: 1, dur: 8.5,  delay: 0.5,  color: '#0891B2' },
  { left: 91, top: 27, size: 6,  opacity: 0.55, blur: 0, dur: 10.7, delay: 5.8,  color: '#22D3EE' },
  { left: 86, top: 45, size: 12, opacity: 0.34, blur: 2, dur: 9.0,  delay: 2.4,  color: '#2C5FA8' },
  { left: 94, top: 62, size: 8,  opacity: 0.48, blur: 1, dur: 7.3,  delay: 7.9,  color: '#0891B2' },
  { left: 82, top: 78, size: 10, opacity: 0.40, blur: 2, dur: 11.4, delay: 4.3,  color: '#22D3EE' },
  { left: 97, top: 90, size: 6,  opacity: 0.45, blur: 0, dur: 8.8,  delay: 1.1,  color: '#1E4480' },
  { left: 88, top: 96, size: 8,  opacity: 0.36, blur: 1, dur: 9.5,  delay: 6.6,  color: '#0891B2' },
  { left: 93, top: 5,  size: 14, opacity: 0.28, blur: 3, dur: 12.1, delay: 8.7,  color: '#2C5FA8' },
];

function FloatingDots() {
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10, overflow: 'hidden' }}>
      {DOT_DATA.map((d, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: d.size,
            height: d.size,
            borderRadius: '50%',
            background: d.color,
            '--o': d.opacity,
            opacity: d.opacity,
            filter: d.blur > 0 ? `blur(${d.blur}px)` : undefined,
            animation: `floatDot ${d.dur}s ease-in-out ${d.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ── NN Logo — glass style for dark background ──────────────────────────────────
function NNLogo({ size = 64 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `1.5px solid rgba(255,255,255,0.25)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(255,255,255,0.08)',
      backdropFilter: 'blur(4px)',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: size * 0.36, fontWeight: 800, letterSpacing: -1, fontFamily: 'Georgia, serif' }}>
        <span style={{ color: '#fff' }}>N</span>
        <span style={{ color: AQUA_BRIGHT }}>N</span>
      </span>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ── Inline SVG icons for password checklist ─────────────────────────────────────
function CheckIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
}
function CircleIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="3"><circle cx="12" cy="12" r="9"/></svg>;
}

// ── Reusable password checklist ─────────────────────────────────────────────────
function PasswordChecklist({ rules, allMet }) {
  return (
    <div style={{
      marginBottom: 16, padding: '12px 14px',
      background: '#f8fafc', borderRadius: 10,
      border: `1px solid ${allMet ? 'rgba(16,185,129,0.3)' : '#e2e8f0'}`,
      transition: 'border-color 0.2s',
    }}>
      {rules.map((rule) => (
        <div key={rule.label} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 12, lineHeight: '22px',
          color: rule.met ? '#059669' : '#94a3b8',
          transition: 'color 0.15s',
        }}>
          {rule.met ? <CheckIcon /> : <CircleIcon />}
          {rule.label}
        </div>
      ))}
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const {
    resolveEmailDomain, signIn, completeNewPassword,
    forgotPassword, confirmPasswordReset,
    error, clearError,
  } = useAuth();

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Forgot / reset password state
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');

  const displayError = localError || error;
  const clearErrors = () => { setLocalError(''); setLocalSuccess(''); clearError(); };

  const inputStyle = (field) => ({
    width: '100%',
    padding: '12px 16px',
    borderRadius: 10,
    border: `1.5px solid ${focusedField === field ? ACCENT : '#d0d7e8'}`,
    background: '#fff',
    color: '#1e293b',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    boxShadow: focusedField === field ? `0 0 0 3px rgba(44,95,168,0.12)` : '0 1px 3px rgba(0,0,0,0.06)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  });

  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 700,
    color: '#475569', marginBottom: 7, letterSpacing: 0.6, textTransform: 'uppercase',
  };

  const btnStyle = {
    width: '100%', padding: '13px',
    borderRadius: 10, border: 'none',
    background: `linear-gradient(135deg, ${ROYAL} 0%, #1a3d6e 100%)`,
    color: '#fff', fontSize: 15, fontWeight: 700,
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.75 : 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    boxShadow: `0 4px 14px rgba(44,95,168,0.40)`,
    transition: 'opacity 0.15s, box-shadow 0.15s',
  };

  const errorBoxStyle = {
    background: '#fef2f2', border: '1.5px solid #fecaca',
    borderRadius: 10, padding: '11px 14px',
    fontSize: 13, color: '#dc2626', marginBottom: 20,
    display: 'flex', alignItems: 'flex-start', gap: 8,
  };

  const successBoxStyle = {
    background: '#f0fdf4', border: '1.5px solid #bbf7d0',
    borderRadius: 10, padding: '11px 14px',
    fontSize: 13, color: '#16a34a', marginBottom: 20,
    display: 'flex', alignItems: 'flex-start', gap: 8,
  };

  const secondaryLinkStyle = {
    background: 'none', border: 'none', color: '#94a3b8', fontSize: 12,
    cursor: 'pointer', padding: 0, marginTop: 18, display: 'block',
    textAlign: 'center', width: '100%',
  };

  // ── Password toggle button ──────────────────────────────────────────────────
  function PasswordToggle() {
    return (
      <button type="button" onClick={() => setShowPassword(v => !v)} style={{
        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
        background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0,
      }}>
        {showPassword
          ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
          : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        }
      </button>
    );
  }

  // ── Step handlers ────────────────────────────────────────────────────────────
  const handleEmailContinue = (e) => {
    e.preventDefault(); clearErrors();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) { setLocalError('Please enter a valid email address.'); return; }
    const tenant = resolveEmailDomain(trimmed);
    if (!tenant) { setLocalError('Your organization is not set up on NexoNurse. Contact your administrator.'); return; }
    if (!tenant.userPoolId) { setLocalError('Authentication is not configured yet. Contact your administrator.'); return; }
    setEmail(trimmed);
    setStep(STEP.PASSWORD);
  };

  const handleForgotPasswordSubmit = async () => {
    clearErrors();
    setIsSubmitting(true);
    try {
      await forgotPassword(email);
      setStep(STEP.RESET_PASSWORD);
    } catch { } finally { setIsSubmitting(false); }
  };

  const handleSignIn = async (e) => {
    e.preventDefault(); clearErrors();
    if (!password) { setLocalError('Please enter your password.'); return; }
    setIsSubmitting(true);
    try {
      const result = await signIn(email, password);
      if (result.success) navigate('/', { replace: true });
      else if (result.challenge === AUTH_CHALLENGE.NEW_PASSWORD_REQUIRED) setStep(STEP.NEW_PASSWORD);
      else if (result.challenge === AUTH_CHALLENGE.PASSWORD_RESET_REQUIRED) handleForgotPasswordSubmit();
    } catch { } finally { setIsSubmitting(false); }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault(); clearErrors();
    const rules = getPwdRules(newPassword, password);
    if (rules.some(r => !r.met)) { setLocalError('Please meet all password requirements below.'); return; }
    if (newPassword !== newPasswordConfirm) { setLocalError('Passwords do not match.'); return; }
    setIsSubmitting(true);
    try {
      const result = await completeNewPassword(newPassword);
      if (result.success) navigate('/', { replace: true });
    } catch { } finally { setIsSubmitting(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault(); clearErrors();
    const rules = getPwdRules(resetPassword);
    if (rules.some(r => !r.met)) { setLocalError('Please meet all password requirements below.'); return; }
    if (resetPassword !== resetPasswordConfirm) { setLocalError('Passwords do not match.'); return; }
    if (!resetCode || resetCode.length < 6) { setLocalError('Please enter the 6-digit verification code from your email.'); return; }
    setIsSubmitting(true);
    try {
      await confirmPasswordReset(resetCode, resetPassword);
      // Reset succeeded — return to sign-in
      setPassword('');
      setResetCode('');
      setResetPassword('');
      setResetPasswordConfirm('');
      setStep(STEP.PASSWORD);
      setLocalSuccess('Password reset successfully. Sign in with your new password.');
    } catch { } finally { setIsSubmitting(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', position: 'relative',
      // Two-tone split: dark navy left (1/3), slightly lighter navy right
      background: `linear-gradient(to right, ${NAVY} 0%, ${NAVY} 33.33%, #0F1B3D 33.33%, #131F3A 100%)`,
      fontFamily: 'var(--font-body, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)',
    }}>

      {/* ── Floating dots — full page ────────────────────────────────────── */}
      <FloatingDots />

      {/* ── Left brand panel ─────────────────────────────────────────────── */}
      <div style={{
        flex: '0 0 33.33%', position: 'relative', zIndex: 1,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '56px 52px',
      }}
        className="login-brand-panel"
      >
        {/* Glowing vertical divider on right edge of left panel */}
        <div style={{
          position: 'absolute', right: 0, top: '8%', bottom: '8%', width: 1,
          background: `linear-gradient(to bottom,
            transparent 0%,
            rgba(34,211,238,0.35) 25%,
            rgba(44,95,168,0.70) 55%,
            rgba(34,211,238,0.25) 80%,
            transparent 100%)`,
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 52 }}>
            <NNLogo size={68} />
            <div>
              <div style={{
                fontFamily: 'var(--font-heading, Georgia, serif)',
                fontSize: 26, fontWeight: 700,
                color: '#fff', letterSpacing: 0.2, lineHeight: 1.2,
              }}>NexoNurse</div>
              <div style={{
                fontSize: 11, color: AQUA_BRIGHT, letterSpacing: 1.4,
                marginTop: 3, fontWeight: 600, textTransform: 'uppercase',
              }}>
                Command Center
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div>
            <h2 style={{
              fontSize: 30, fontWeight: 700, color: '#fff',
              lineHeight: 1.3, margin: '0 0 16px',
              fontFamily: 'var(--font-heading, Georgia, serif)',
            }}>
              Less Noise.<br />More Impact.<br />Better Care.
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.60)', lineHeight: 1.7, margin: 0 }}>
              Bringing what matters today front and center.
            </p>
          </div>
        </div>
      </div>

      {/* ── Right form panel ─────────────────────────────────────────────── */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px', position: 'relative', zIndex: 1,
      }}>
        <div style={{
          width: '100%', maxWidth: 420,
          background: '#fff',
          borderRadius: 20,
          padding: '44px 40px 36px',
          boxShadow: '0 8px 48px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>

          {/* Success message */}
          {localSuccess && (
            <div style={successBoxStyle}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              {localSuccess}
            </div>
          )}

          {/* Error */}
          {displayError && (
            <div style={errorBoxStyle}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {displayError}
            </div>
          )}

          {/* ── Step: Email ───────────────────────────────────────────────── */}
          {step === STEP.EMAIL && (
            <form onSubmit={handleEmailContinue} noValidate>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Sign in</div>
                <div style={{ fontSize: 14, color: '#64748b' }}>Enter your work email to continue.</div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <input
                  type="email" autoFocus autoComplete="email"
                  value={email}
                  onChange={(e) => { clearErrors(); setEmail(e.target.value); }}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="you@nexonurse.com"
                  style={inputStyle('email')}
                  required
                />
              </div>

              <button type="submit" style={btnStyle} disabled={isSubmitting}>
                {isSubmitting ? <><Spinner />Checking…</> : 'Continue →'}
              </button>
            </form>
          )}

          {/* ── Step: Password ────────────────────────────────────────────── */}
          {step === STEP.PASSWORD && (
            <form onSubmit={handleSignIn} noValidate>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Welcome back</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  Signing in as{' '}
                  <span style={{ color: ROYAL, fontWeight: 600 }}>{email}</span>
                </div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoFocus autoComplete="current-password"
                    value={password}
                    onChange={(e) => { clearErrors(); setPassword(e.target.value); }}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your password"
                    style={{ ...inputStyle('password'), paddingRight: 44 }}
                    required
                  />
                  <PasswordToggle />
                </div>
              </div>

              <button type="submit" style={btnStyle} disabled={isSubmitting}>
                {isSubmitting ? <><Spinner />Signing in…</> : 'Sign in'}
              </button>

              <button type="button" onClick={() => { clearErrors(); setStep(STEP.FORGOT_PASSWORD); }} style={{
                background: 'none', border: 'none', color: ROYAL, fontSize: 13,
                cursor: 'pointer', padding: 0, marginTop: 14, display: 'block',
                textAlign: 'center', width: '100%', fontWeight: 500,
              }}>
                Forgot password?
              </button>

              <button type="button" onClick={() => { clearErrors(); setStep(STEP.EMAIL); setPassword(''); }} style={secondaryLinkStyle}>
                ← Use a different email
              </button>
            </form>
          )}

          {/* ── Step: New password (first-time login) ──────────────────────── */}
          {step === STEP.NEW_PASSWORD && (() => {
            const rules = getPwdRules(newPassword, password);
            const allMet = rules.every(r => r.met);
            const showChecklist = newPassword.length > 0;
            return (
              <form onSubmit={handleNewPassword} noValidate>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Set your password</div>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Choose a permanent password to secure your account.</div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>New password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'} autoFocus autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => { clearErrors(); setNewPassword(e.target.value); }}
                      onFocus={() => setFocusedField('new1')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="At least 12 characters"
                      style={{ ...inputStyle('new1'), paddingRight: 44 }} required
                    />
                    <PasswordToggle />
                  </div>
                </div>

                {showChecklist && <PasswordChecklist rules={rules} allMet={allMet} />}

                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Confirm new password</label>
                  <input
                    type="password" autoComplete="new-password"
                    value={newPasswordConfirm}
                    onChange={(e) => { clearErrors(); setNewPasswordConfirm(e.target.value); }}
                    onFocus={() => setFocusedField('new2')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Repeat your new password"
                    style={{
                      ...inputStyle('new2'),
                      borderColor: newPasswordConfirm.length > 0 && newPasswordConfirm !== newPassword
                        ? '#fca5a5'
                        : (focusedField === 'new2' ? ACCENT : '#d0d7e8'),
                    }} required
                  />
                  {newPasswordConfirm.length > 0 && newPasswordConfirm !== newPassword && (
                    <div style={{ fontSize: 12, color: '#dc2626', marginTop: 5 }}>Passwords do not match.</div>
                  )}
                </div>

                <button type="submit" style={{ ...btnStyle, opacity: (isSubmitting || !allMet) ? 0.6 : 1, cursor: (!allMet || isSubmitting) ? 'not-allowed' : 'pointer' }} disabled={isSubmitting || !allMet}>
                  {isSubmitting ? <><Spinner />Setting password…</> : 'Set password & sign in'}
                </button>
              </form>
            );
          })()}

          {/* ── Step: Forgot password — send code ──────────────────────────── */}
          {step === STEP.FORGOT_PASSWORD && (
            <form onSubmit={(e) => { e.preventDefault(); handleForgotPasswordSubmit(); }} noValidate>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: `rgba(44,95,168,0.08)`, border: `1.5px solid rgba(44,95,168,0.2)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ROYAL} strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Reset your password</div>
                <div style={{ fontSize: 14, color: '#64748b' }}>
                  We&apos;ll send a verification code to{' '}
                  <span style={{ color: ROYAL, fontWeight: 600 }}>{email}</span>
                </div>
              </div>

              <button type="submit" style={btnStyle} disabled={isSubmitting}>
                {isSubmitting ? <><Spinner />Sending code…</> : 'Send reset code'}
              </button>

              <button type="button" onClick={() => { clearErrors(); setStep(STEP.PASSWORD); }} style={secondaryLinkStyle}>
                ← Back to sign in
              </button>
            </form>
          )}

          {/* ── Step: Reset password — enter code + new password ────────────── */}
          {step === STEP.RESET_PASSWORD && (() => {
            const rules = getPwdRules(resetPassword);
            const allMet = rules.every(r => r.met);
            const showChecklist = resetPassword.length > 0;
            return (
              <form onSubmit={handleResetPassword} noValidate>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Create a new password</div>
                  <div style={{ fontSize: 14, color: '#64748b' }}>Enter the code sent to your email and choose a new password.</div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Verification code</label>
                  <input
                    type="text" autoFocus autoComplete="one-time-code"
                    inputMode="numeric" maxLength={6}
                    value={resetCode}
                    onChange={(e) => { clearErrors(); setResetCode(e.target.value.replace(/\D/g, '')); }}
                    onFocus={() => setFocusedField('code')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="6-digit code"
                    style={{ ...inputStyle('code'), letterSpacing: 8, fontSize: 20, textAlign: 'center', fontWeight: 700 }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>New password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                      value={resetPassword}
                      onChange={(e) => { clearErrors(); setResetPassword(e.target.value); }}
                      onFocus={() => setFocusedField('reset1')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="At least 12 characters"
                      style={{ ...inputStyle('reset1'), paddingRight: 44 }} required
                    />
                    <PasswordToggle />
                  </div>
                </div>

                {showChecklist && <PasswordChecklist rules={rules} allMet={allMet} />}

                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Confirm new password</label>
                  <input
                    type="password" autoComplete="new-password"
                    value={resetPasswordConfirm}
                    onChange={(e) => { clearErrors(); setResetPasswordConfirm(e.target.value); }}
                    onFocus={() => setFocusedField('reset2')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Repeat your new password"
                    style={{
                      ...inputStyle('reset2'),
                      borderColor: resetPasswordConfirm.length > 0 && resetPasswordConfirm !== resetPassword
                        ? '#fca5a5'
                        : (focusedField === 'reset2' ? ACCENT : '#d0d7e8'),
                    }} required
                  />
                  {resetPasswordConfirm.length > 0 && resetPasswordConfirm !== resetPassword && (
                    <div style={{ fontSize: 12, color: '#dc2626', marginTop: 5 }}>Passwords do not match.</div>
                  )}
                </div>

                <button type="submit" style={{ ...btnStyle, opacity: (isSubmitting || !allMet) ? 0.6 : 1, cursor: (!allMet || isSubmitting) ? 'not-allowed' : 'pointer' }} disabled={isSubmitting || !allMet}>
                  {isSubmitting ? <><Spinner />Resetting password…</> : 'Reset password'}
                </button>

                <button type="button" onClick={handleForgotPasswordSubmit} style={{
                  background: 'none', border: 'none', color: ROYAL, fontSize: 13,
                  cursor: 'pointer', padding: 0, marginTop: 14, display: 'block',
                  textAlign: 'center', width: '100%', fontWeight: 500,
                }}>
                  Resend code
                </button>

                <button type="button" onClick={() => { clearErrors(); setResetCode(''); setResetPassword(''); setResetPasswordConfirm(''); setStep(STEP.PASSWORD); }} style={secondaryLinkStyle}>
                  ← Back to sign in
                </button>
              </form>
            );
          })()}

          {/* Footer */}
          <div style={{
            marginTop: 28, paddingTop: 20,
            borderTop: '1px solid #f1f5f9',
            textAlign: 'center', fontSize: 12, color: '#94a3b8',
          }}>
            Having trouble?{' '}
            <a href="mailto:support@nexonurse.com" style={{ color: ROYAL, textDecoration: 'none', fontWeight: 500 }}>
              support@nexonurse.com
            </a>
          </div>
        </div>
      </div>

      {/* ── Styles ───────────────────────────────────────────────────────────── */}
      <style>{`
        @keyframes floatDot {
          0%, 100% { transform: translateY(0) translateX(0);  opacity: var(--o); }
          33%       { transform: translateY(-8px) translateX(4px);  opacity: calc(var(--o) * 1.3); }
          66%       { transform: translateY(5px) translateX(-6px); opacity: calc(var(--o) * 0.8); }
        }
        @media (max-width: 768px) {
          .login-brand-panel { display: none !important; }
        }
        input::placeholder { color: #94a3b8; }
        input:focus { outline: none; }
      `}</style>
    </div>
  );
}
